import { ApiService } from "./api.service";
import { uploadToPresignedUrl } from "./presigned-upload";

type TWorkspaceAssetEntityType = "WORKSPACE_LOGO" | "INTAKE_RICH_TEXT_IMAGE";

export interface IWorkspaceAssetPresignedResponse {
  upload_data: {
    url: string;
    fields?: Record<string, string>;
    method?: "PUT" | "POST";
  };
  asset_id: string;
  asset_url: string;
}

interface ICreateAssetPayload {
  entity_identifier: string;
  entity_type: TWorkspaceAssetEntityType;
  name: string;
  type: string;
  size: number;
}

export class WorkspaceAssetService extends ApiService {
  async createAsset(
    slug: string,
    data: ICreateAssetPayload,
  ): Promise<IWorkspaceAssetPresignedResponse> {
    return this.post(`/api/assets/v2/workspaces/${slug}/`, data);
  }

  async uploadToPresignedUrl(
    presigned: IWorkspaceAssetPresignedResponse["upload_data"],
    file: File,
  ): Promise<void> {
    await uploadToPresignedUrl(presigned, file);
  }

  async markUploaded(slug: string, assetId: string): Promise<void> {
    return this.patch(`/api/assets/v2/workspaces/${slug}/${assetId}/`);
  }

  async deleteAsset(slug: string, assetId: string): Promise<void> {
    return this.delete(`/api/assets/v2/workspaces/${slug}/${assetId}/`);
  }

  async uploadWorkspaceLogo(
    slug: string,
    workspaceId: string,
    file: File,
  ): Promise<IWorkspaceAssetPresignedResponse> {
    const presigned = await this.createAsset(slug, {
      entity_identifier: workspaceId,
      entity_type: "WORKSPACE_LOGO",
      name: file.name,
      type: file.type || "image/jpeg",
      size: file.size,
    });
    await this.uploadToPresignedUrl(presigned.upload_data, file);
    await this.markUploaded(slug, presigned.asset_id);
    return presigned;
  }

  /** Upload an image embedded in an intake Rich Text page element (#3891).
   *  The asset is workspace-scoped and owned by the form (`formId`), never by a
   *  submission — so it is not carried into a project created from the request,
   *  unlike a `kind: file` upload. Resolves to the asset URL to embed. */
  async uploadRichTextImage(slug: string, formId: string, file: File): Promise<string> {
    const presigned = await this.createAsset(slug, {
      entity_identifier: formId,
      entity_type: "INTAKE_RICH_TEXT_IMAGE",
      name: file.name,
      type: file.type || "image/png",
      size: file.size,
    });
    await this.uploadToPresignedUrl(presigned.upload_data, file);
    await this.markUploaded(slug, presigned.asset_id);
    return presigned.asset_url;
  }
}
